import React from 'react';
import type { TFunction } from 'i18next';
import './MetaTagPills.sass';

/** Libellés et valeurs : `models.item` (partie prenante), pas les abréviations situation_analysis. */
const impactLevel = (t: TFunction, val: string) =>
	t(`models.item.fields.impact.level.${val}`, { defaultValue: val });

const trendLevel = (t: TFunction, val: string) =>
	t(`models.item.fields.trend.level.${val}`, { defaultValue: val });

const engagementLevel = (t: TFunction, val: string) => {
	if (val === 'inactive') return t('models.item.levels.inactive_neutral');
	return t(`models.item.levels.${val}`, { defaultValue: val });
};

const polarityLevel = (t: TFunction, val: string) =>
	t(`models.item.levels.${val}`, { defaultValue: val });

export interface StakeholderMetaPillsProps {
	item: any;
	t: TFunction;
	/** e.g. `item-tags stakeholder-meta-pills` for AI modal */
	className?: string;
}

/**
 * Pastilles alignées sur le système meta-tag-pill (importance, propension, engagement).
 */
export const StakeholderMetaPills: React.FC<StakeholderMetaPillsProps> = ({
	item,
	t,
	className = 'stakeholder-meta-pills'
}) => {
	const pills: React.ReactNode[] = [];

	if (item.impact) {
		pills.push(
			<span
				key="impact"
				className={`meta-tag-pill meta-tag-pill--sh-impact meta-tag-pill--sh-impact-${item.impact}`}
			>
				<span className="meta-tag-pill__lbl">{t('models.item.fields.impact.label')}</span>
				<span className="meta-tag-pill__sep" aria-hidden>
					·
				</span>
				<span className="meta-tag-pill__val">{impactLevel(t, item.impact)}</span>
			</span>
		);
	}
	if (item.trend) {
		pills.push(
			<span
				key="trend"
				className={`meta-tag-pill meta-tag-pill--sh-propensity meta-tag-pill--sh-prop-${item.trend}`}
			>
				<span className="meta-tag-pill__lbl">{t('models.item.fields.trend.label')}</span>
				<span className="meta-tag-pill__sep" aria-hidden>
					·
				</span>
				<span className="meta-tag-pill__val">{trendLevel(t, item.trend)}</span>
			</span>
		);
	}
	if (item.action_level) {
		let engagementText = engagementLevel(t, item.action_level);
		if (
			item.action_polarity &&
			item.action_level !== 'neutral' &&
			item.action_level !== 'inactive'
		) {
			engagementText = `${engagementText} · ${polarityLevel(t, item.action_polarity)}`;
		}
		pills.push(
			<span
				key="engagement"
				className={`meta-tag-pill meta-tag-pill--sh-engagement meta-tag-pill--sh-pol-${item.action_polarity || 'neutral'}`}
			>
				<span className="meta-tag-pill__lbl">{t('models.item.engagement')}</span>
				<span className="meta-tag-pill__sep" aria-hidden>
					·
				</span>
				<span className="meta-tag-pill__val">{engagementText}</span>
			</span>
		);
	}

	if (pills.length === 0) return null;

	return <div className={className}>{pills}</div>;
};
